import router from '@/router';
import {
  ORDER_SERVICES__AVAILABLE_SERVICES,
  ORDER_SERVICES__GET_SERVICES,
  ORDER_SERVICES__MUTATE_LOADING_STATUS,
  ORDER_SERVICES__MUTATE_SERVICES,
  ORDER_SERVICES__RESET,
  ORDER_SERVICES__SELECTED_SERVICE,
  ORDER_SERVICES__SUBMIT_PRICED_LTRS_SERVICE,
  ORDER_SERVICES__SUBMIT_QUOTE,
  SCHEDULES__MUTATE_SCHEDULE,
} from '@/store/types';
import _ from 'lodash';
import { OrderServicesConstants } from 'ls-constants';
import { lsApiService, lsErrorService, lsToastService } from 'ls-vue-common';
import Vue from 'vue';
import { lsModuleHelpers } from '../helpers';
import { lsOrderServicesStoreHelpers } from './order-services.helpers';

const createInitialState = () => {
  return {
    servicesByMarketId: {},
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [ORDER_SERVICES__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [ORDER_SERVICES__MUTATE_SERVICES](state, { market_id, services }) {
    Vue.set(state.servicesByMarketId, market_id, _.keyBy(services, 'service_id'));
  },

  [ORDER_SERVICES__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
};

export const actions = {
  [ORDER_SERVICES__GET_SERVICES](
    { commit, rootState },
    { forceRefresh = false, property_id } = {}
  ) {
    let requestPromise = Promise.resolve();

    const { properties, orderServices } = rootState;

    // if property_id is not found, we've routed to a property that doesn't exist
    if (!_.get(properties.propertiesById, `${property_id}`)) {
      return router.push({
        name: 'services',
      });
    }

    const market_id = _.get(properties.propertiesById, `${property_id}.market_id`);

    // if market_id is not found, don't call api but stay on page
    if (_.isNil(market_id)) {
      return;
    }

    if (_.isEmpty(orderServices.servicesByMarketId[market_id]) || forceRefresh) {
      commit(ORDER_SERVICES__MUTATE_LOADING_STATUS, {
        isLoading: true,
      });

      requestPromise = lsApiService.request({
        method: 'GET',
        route: `/services`,
        params: {
          is_requestable: 1,
          market_id,
        },
      });
      return requestPromise
        .then(({ data }) => {
          commit(ORDER_SERVICES__MUTATE_SERVICES, {
            market_id,
            services: data,
          });
        })
        .catch((error) => {
          lsErrorService.log({ error, showAlert: true });
        })
        .finally(() => {
          commit(ORDER_SERVICES__MUTATE_LOADING_STATUS, {
            isLoading: false,
          });
        });
    }
    return requestPromise;
  },

  [ORDER_SERVICES__SUBMIT_QUOTE]({ commit, rootState }, { property_id, service_id, formData }) {
    commit(ORDER_SERVICES__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const payload = lsOrderServicesStoreHelpers.formatQuotePayload({
      pendingQuote: { ...formData, service_id },
    });

    const { customer_id } = rootState.auth.user;
    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/bulk`,
        payload,
      })
      .then(({ data }) => {
        // optimistically update schedules,
        commit(SCHEDULES__MUTATE_SCHEDULE, _.head(data.schedules));
        router.push({ name: 'services' });
        lsToastService.open({
          message: 'Your request has been submitted.',
          type: 'is-success',
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ORDER_SERVICES__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  // lawntreatment / aeration
  [ORDER_SERVICES__SUBMIT_PRICED_LTRS_SERVICE](
    { commit, rootState },
    { service_id, price_id, starts_at, property_id, service_promo_id = null, onSuccess }
  ) {
    commit(ORDER_SERVICES__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/bulk`,
        payload: {
          schedules: [{ service_id, price_id, starts_at, service_promo_id }],
        },
      })
      .then(({ data }) => {
        commit(SCHEDULES__MUTATE_SCHEDULE, _.head(data.schedules));
        onSuccess();
        lsToastService.open({
          message: 'Your order has been submitted.',
          type: 'is-success',
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ORDER_SERVICES__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
};

export const getters = {
  [ORDER_SERVICES__AVAILABLE_SERVICES]: (state, getters, rootState) => (property_id) => {
    const {
      market_id,
      priced,
      can_request_additional_services,
    } = rootState.properties.propertiesById[property_id];

    const filteredServices = _.filter(
      state.servicesByMarketId[market_id],
      ({ is_ltrs, str_id, service_id }) => {
        // if service is ltrs but doesn't have pricing OR if service does not exist in constants file - don't return it
        const serviceById = _.get(OrderServicesConstants.orderableServices, service_id);
        const isOrderableWithoutAssignedPro =
          _.get(serviceById, 'isOrderableWithoutAssignedPro') ||
          service_id === 10 ||
          service_id === 170;

        return (
          !(is_ltrs && !_.get(priced, `services.${str_id}.options[0]`)) &&
          serviceById &&
          // if no assigned pro, return services that are orderable without assigned pro
          (can_request_additional_services || isOrderableWithoutAssignedPro)
        );
      }
    );
    return _.orderBy(filteredServices, ({ service_id }) => {
      return _.get(
        // default to 100 if not found so it displays at the bottom
        OrderServicesConstants.orderableServices,
        `${service_id}.displayOrder`,
        100
      );
    });
  },
  [ORDER_SERVICES__SELECTED_SERVICE]: (state, getters) => (property_id, service_id) => {
    return _.find(getters[ORDER_SERVICES__AVAILABLE_SERVICES](property_id), {
      service_id: _.toNumber(service_id),
    });
  },
};

export const orderServices = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
